import { useState } from "react";
import { Geolocation } from "@capacitor/geolocation";

import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "./useDeviceType";
import { AddressInfo } from "../models/User";

interface Coordinates {
  lat: number;
  lng: number;
}

interface LocationResult {
  currentLocation: Coordinates;
  currentState?: string;
  addressObject?: AddressInfo;
}

const OPEN_STREET_MAP = "https://nominatim.openstreetmap.org";

const useCurrentLocation = () => {
  const [position, setPosition] = useState<Coordinates | undefined>(undefined);
  const [currentState, setCurrentState] = useState<string>();
  const [addressObject, setAddressObject] = useState<AddressInfo | null>(null);
  const { isDesktop } = useDeviceType();

  const fetchLocation = async (): Promise<LocationResult> => {
    try {
      let pos: GeolocationPosition;
      if (isDesktop) {
        if ("geolocation" in navigator) {
          pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
        } else {
          throw new Error("Geolocation is not available in your browser.");
        }
      } else {
        pos = (await Geolocation.getCurrentPosition()) as unknown as GeolocationPosition;
      }

      const newPosition: Coordinates = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      };
      setPosition(newPosition);

      const response = await fetch(
        `${OPEN_STREET_MAP}/reverse?lat=${newPosition.lat}&lon=${newPosition.lng}&format=json`
      );
      const data = await response.json();

      let formattedAddressObject: AddressInfo | null = null;
      if (data && data.address) {
        const addr = data.address;
        const address = addr.road || addr.pedestrian || addr.residential || "";
        const city = addr.city || addr.town || addr.village || addr.hamlet || "";
        const state = addr.state || "";
        const zip = addr.postcode || "";
        const country = addr.country || "";
        const formattedAddress = [address, city, state, zip, country]
          .filter((item) => !!item)
          .join(", ");
        formattedAddressObject = {
          address,
          city,
          state,
          zip,
          country,
          formattedAddress
        };
      } else {
        showErrorMessage("Unable to retrieve formatted address information.");
      }
      setAddressObject(formattedAddressObject);

      const stateValue = formattedAddressObject ? formattedAddressObject.state : undefined;
      if (!stateValue) {
        showErrorMessage("Unable to retrieve state information.");
      }
      setCurrentState(stateValue);

      return {
        currentLocation: newPosition,
        currentState: stateValue,
        addressObject: formattedAddressObject ?? {}
      };
    } catch (error: any) {
      showErrorMessage(error.message);
      throw error;
    }
  };

  return {
    currentLocation: position,
    currentState,
    addressObject,
    refreshLocation: fetchLocation
  };
};

export default useCurrentLocation;
